<template>
  <div class="agreement-compare-page">
    <div class="bg-white table-shadow p-4">
      <div class="mb-4">
        <h1 class="mb-2">Shipping agreements comparison</h1>
        <p class="text-muted mb-0">
          If you have your own agreement with carrier, here you can check if your prices are better than ours.
          Currently we support using own agreements only for Parcel distribution channel.
        </p>
      </div>

      <VeeForm @submit="handleSubmit">
        <section class="form-section">
          <div class="row g-4 align-items-start">
            <div class="col-12 col-xl-6 agreement-card">
              <h2 class="section-title">Your agreement with carrier</h2>
              <div class="row g-3 align-items-end">
                <div class="col-md-7">
                  <Field
                    v-slot="{ field: control, errors }"
                    name="customerNumber"
                    :rules="{ required: true }"
                    :model-value="form.customerNumber"
                  >
                    <div class="form-group position-relative mb-0">
                      <label for="customer-number" class="form-label">Customer number</label>
                      <input
                        id="customer-number"
                        type="text"
                        name="customerNumber"
                        class="form-control"
                        :class="{ 'is-invalid': errors.length }"
                        :value="control.value"
                        autocomplete="off"
                        @input="updateTextField(control, 'customerNumber', $event)"
                        @blur="control.onBlur"
                      />
                      <div v-if="errors.length" class="invalid-feedback">{{ formatErrors(errors) }}</div>
                    </div>
                  </Field>
                </div>
                <div class="col-md-5">
                  <Field
                    v-slot="{ field: control, errors }"
                    name="carrierHandle"
                    :rules="{ required: true }"
                    :model-value="form.carrierHandle"
                  >
                    <div class="form-group position-relative mb-0">
                      <label class="form-label" for="carrier-select">Carrier</label>
                      <VSelect
                        id="carrier-select"
                        :value="control.value"
                        name="carrierHandle"
                        :options="carrierOptions"
                        fieldset-class="form-group position-relative mb-0"
                        @update:model-value="(value: string) => updateSelectField(control, 'carrierHandle', value)"
                      />
                      <div v-if="errors.length" class="invalid-feedback d-block">{{ formatErrors(errors) }}</div>
                    </div>
                  </Field>
                </div>
              </div>
            </div>
            <div class="col-12 col-xl-6 shipment-padding">
              <h2 class="section-title">What you are sending</h2>
              <div class="row g-3">
                <div v-for="dimension in shipmentFields" :key="dimension.id" :class="dimension.colClass">
                  <Field
                    v-slot="{ field: control, errors }"
                    :name="dimension.name"
                    :rules="dimension.rules"
                    :model-value="form[dimension.model]"
                  >
                    <div class="form-group mb-0">
                      <label class="form-label" :for="dimension.id">{{ dimension.label }}</label>
                      <input
                        :id="dimension.id"
                        :type="dimension.type"
                        class="form-control"
                        :class="{ 'is-invalid': errors.length }"
                        :value="control.value"
                        @input="updateTextField(control, dimension.model as keyof AgreementForm, $event)"
                        @blur="control.onBlur"
                      />
                      <div v-if="errors.length" class="invalid-feedback">{{ formatErrors(errors) }}</div>
                    </div>
                  </Field>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="form-section">
          <div class="row g-4 location-pair">
            <div class="col-12 col-xl-6">
              <h2 class="section-title">From where</h2>
              <div class="row g-3 location-row">
                <div v-for="field in senderFields" :key="field.id" :class="field.colClass">
                  <Field
                    v-slot="{ field: control, errors }"
                    :name="field.name"
                    :rules="field.rules"
                    :model-value="form[field.model]"
                  >
                    <div class="form-group mb-0">
                      <label class="form-label" :for="field.id">{{ field.label }}</label>
                      <input
                        :id="field.id"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': errors.length }"
                        :value="control.value"
                        v-bind="field.attrs"
                        @input="updateTextField(control, field.model as keyof AgreementForm, $event)"
                        @blur="control.onBlur"
                      />
                      <div v-if="errors.length" class="invalid-feedback">{{ formatErrors(errors) }}</div>
                    </div>
                  </Field>
                </div>
              </div>
            </div>
            <div class="col-12 col-xl-6">
              <h2 class="section-title">To where</h2>
              <div class="row g-3 location-row">
                <div v-for="field in recipientFields" :key="field.id" :class="field.colClass">
                  <Field
                    v-slot="{ field: control, errors }"
                    :name="field.name"
                    :rules="field.rules"
                    :model-value="form[field.model]"
                  >
                    <div class="form-group mb-0">
                      <label class="form-label" :for="field.id">{{ field.label }}</label>
                      <input
                        :id="field.id"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': errors.length }"
                        :value="control.value"
                        v-bind="field.attrs"
                        @input="updateTextField(control, field.model as keyof AgreementForm, $event)"
                        @blur="control.onBlur"
                      />
                      <div v-if="errors.length" class="invalid-feedback">{{ formatErrors(errors) }}</div>
                    </div>
                  </Field>
                </div>
              </div>
            </div>
          </div>
        </section>
        <div class="mt-4 d-flex justify-content-center">
          <b-button type="submit" class="btn btn-green" :disabled="isSubmitDisabled">
            <span v-if="isLoading">Comparing...</span>
            <span v-else>Compare prices</span>
          </b-button>
        </div>
      </VeeForm>

      <div v-if="result" class="mt-5">
        <div class="row gy-4">
          <div class="col-md-6" data-section="provided">
            <div class="result-card p-4 h-100" :class="cardClass('provided')">
              <h3 class="h4 mb-3">Shipping with PacSort</h3>
              <template v-if="result.provided.error">
                <p class="text-danger mb-0">We cannot request quote right now, please try again later</p>
              </template>
              <template v-else>
                <div class="result-cost mb-1">{{ formatCost(result.provided.cost) }}</div>
              </template>
            </div>
          </div>
          <div class="col-md-6" data-section="customer">
            <div class="result-card p-4 h-100" :class="cardClass('customer')">
              <h3 class="h4 mb-3">Shipping with your agreement</h3>
              <template v-if="result.customer.error">
                <p class="text-danger mb-0">{{ getCustomerErrorMessage(result.customer.error) }}</p>
              </template>
              <template v-else>
                <div class="result-cost mb-1">{{ formatCost(result.customer.cost) }}</div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { SelectOptions } from '@/types/Components/SelectOptions'
import type { AgreementFieldControl, AgreementForm, AgreementsComparePayload, AgreementsCompareResponse } from '@/types/Models/Agreements'
import { useNotification } from '@kyvg/vue3-notification'
import { Form as VeeForm, Field } from 'vee-validate'
import { defineComponent, computed, reactive, ref, watch } from 'vue'
import { CARRIERS_NAMES, DHL_FREIGHT } from '@/const/Carriers'
import VSelect from '@/views/Components/Elements/Form/VSelect.vue'
import AgreementsProxy from '@/proxies/AgreementsProxy'

export default defineComponent({
  name: 'AgreementsCompare',
  components: { VeeForm, Field, VSelect },
  setup() {
    const form = reactive<AgreementForm>({
      customerNumber: '',
      carrierHandle: DHL_FREIGHT,
      weight: '50000',
      height: '50',
      width: '50',
      length: '50',
      senderAddressLine: 'Källstorpsvägen 8',
      senderPostalCode: '46157',
      senderCity: 'Trollhättan',
      recipientAddressLine: 'Lundagatan',
      recipientPostalCode: '11727',
      recipientCity: 'Stockholm',
    })

    const isLoading = ref(false)
    const result = ref<AgreementsCompareResponse['data'] | null>(null)
    const { notify } = useNotification()

    const carrierOptions: SelectOptions[] = [
      {
        value: DHL_FREIGHT,
        text: CARRIERS_NAMES[DHL_FREIGHT],
      },
    ]

    const shipmentFields = [
      { id: 'shipment-weight', name: 'weight', label: 'Weight (g)', model: 'weight', type: 'number', rules: 'required|numeric|min_value:1', colClass: 'col-6 col-sm-3 col-md-3 col-xl-3' },
      { id: 'shipment-height', name: 'height', label: 'Height (cm)', model: 'height', type: 'number', rules: 'required|numeric|min_value:1', colClass: 'col-6 col-sm-3 col-md-3 col-xl-3' },
      { id: 'shipment-length', name: 'length', label: 'Length (cm)', model: 'length', type: 'number', rules: 'required|numeric|min_value:1', colClass: 'col-6 col-sm-3 col-md-3 col-xl-3' },
      { id: 'shipment-width', name: 'width', label: 'Width (cm)', model: 'width', type: 'number', rules: 'required|numeric|min_value:1', colClass: 'col-6 col-sm-3 col-md-3 col-xl-3' },
    ]

    const senderFields = [
      { id: 'sender-address', name: 'senderAddressLine', label: 'Address line', model: 'senderAddressLine', colClass: 'col-12 col-lg-6', rules: 'required' },
      {
        id: 'sender-postal',
        name: 'senderPostalCode',
        label: 'Postal code',
        model: 'senderPostalCode',
        colClass: 'col-6 col-sm-4 col-md-3 col-xl-3',
        rules: 'required',
        attrs: { maxlength: 6, inputmode: 'numeric' },
      },
      { id: 'sender-city', name: 'senderCity', label: 'City', model: 'senderCity', colClass: 'col-12 col-sm-8 col-md-3 col-xl-3', rules: 'required' },
    ]

    const recipientFields = [
      { id: 'recipient-address', name: 'recipientAddressLine', label: 'Address line', model: 'recipientAddressLine', colClass: 'col-12 col-lg-6', rules: 'required' },
      {
        id: 'recipient-postal',
        name: 'recipientPostalCode',
        label: 'Postal code',
        model: 'recipientPostalCode',
        colClass: 'col-6 col-sm-4 col-md-3 col-xl-3',
        rules: 'required',
        attrs: { maxlength: 6, inputmode: 'numeric' },
      },
      { id: 'recipient-city', name: 'recipientCity', label: 'City', model: 'recipientCity', colClass: 'col-12 col-sm-8 col-md-3 col-xl-3', rules: 'required' },
    ]

    const requiredStringFields: Array<keyof AgreementForm> = [
      'customerNumber',
      'carrierHandle',
      'senderAddressLine',
      'senderPostalCode',
      'senderCity',
      'recipientAddressLine',
      'recipientPostalCode',
      'recipientCity',
    ]

    const numericFields: Array<keyof AgreementForm> = ['weight', 'height', 'width', 'length']

    const isSubmitDisabled = computed(() => {
      const hasEmptyStrings = requiredStringFields.some((key) => !form[key].trim())
      const hasInvalidNumbers = numericFields.some((key) => Number(form[key]) <= 0)
      return isLoading.value || hasEmptyStrings || hasInvalidNumbers
    })

    const buildOverrides = (): Omit<AgreementsComparePayload, 'customerNumber' | 'carrierHandle'> => {
      return {
        weight: Number(form.weight),
        height: Number(form.height),
        width: Number(form.width),
        length: Number(form.length),
        senderAddressLine: form.senderAddressLine,
        senderPostalCode: form.senderPostalCode,
        senderCity: form.senderCity,
        recipientAddressLine: form.recipientAddressLine,
        recipientPostalCode: form.recipientPostalCode,
        recipientCity: form.recipientCity,
      }
    }

    const updateTextField = (control: AgreementFieldControl, key: keyof AgreementForm, event: Event) => {
      const value = (event.target as HTMLInputElement).value
      control.onChange(value)
      form[key] = value
    }

    const updateSelectField = (control: AgreementFieldControl, key: keyof AgreementForm, value: string) => {
      control.onChange(value)
      form[key] = value
    }

    const handleSubmit = () => {
      if (isSubmitDisabled.value) {
        return
      }

      isLoading.value = true
      result.value = null

      new AgreementsProxy().compare({ ...buildOverrides(), customerNumber: form.customerNumber.trim(), carrierHandle: form.carrierHandle })
        .then((response) => {
          result.value = response.data
        })
        .catch(() => {
          notify({
            group: 'error',
            text: 'Unable to compare agreements right now. Please try again.',
          })
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    const formatCost = (cost: number | null) => {
      if (cost === null || Number.isNaN(Number(cost))) {
        return '—'
      }

      return `${new Intl.NumberFormat('sv-SE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(cost)} SEK`
    }

    const getResultState = (type: 'provided' | 'customer') => {
      const providedCost = result.value?.provided?.cost
      const customerCost = result.value?.customer?.cost

      if (
        providedCost === null ||
        providedCost === undefined ||
        customerCost === null ||
        customerCost === undefined ||
        Number.isNaN(Number(providedCost)) ||
        Number.isNaN(Number(customerCost)) ||
        providedCost === customerCost
      ) {
        return null
      }

      const providedCheaper = Number(providedCost) < Number(customerCost)
      const isSuccess = type === 'provided' ? providedCheaper : !providedCheaper

      return isSuccess
    }

    const cardClass = (type: 'provided' | 'customer') => {
      const state = getResultState(type)

      if (state === null) {
        return {}
      }

      return state ? { 'result-card--success': true } : { 'result-card--danger': true }
    }

    const formatErrors = (errors: string[]) => errors.join('\n')

    const getCustomerErrorMessage = (error: string) => {
      if (error === 'customer_number_not_valid') {
        return 'Customer number is not valid'
      }

      return 'We cannot request quote right now, please try again later'
    }

    watch(
      () => form.customerNumber,
      (value) => {
        if (!value.trim()) {
          result.value = null
        }
      },
    )

    return {
      form,
      isLoading,
      result,
      carrierOptions,
      shipmentFields,
      senderFields,
      recipientFields,
      isSubmitDisabled,
      updateTextField,
      updateSelectField,
      handleSubmit,
      formatCost,
      cardClass,
      formatErrors,
      getCustomerErrorMessage,
    }
  },
})
</script>

<style scoped>
.agreement-compare-page {
  min-height: 70vh;
}

.result-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #fdfdfd;
}

.result-cost {
  font-size: 32px;
  font-weight: 600;
}

.result-card--success {
  border-color: #2fb344;
  background: #f0f8f0;
}

.result-card--danger {
  border-color: #f03e3e;
  background: #fff5f5;
}

.form-section + .form-section {
  margin-top: 2rem;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 1rem;
}

.agreement-card {
  background: #eef0f3;
  border-radius: 8px;
  padding: 1rem;
}

.shipment-padding {
  padding: 1rem;
}

</style>
